import React, { useState, useMemo } from 'react';
import { Plus, AlertCircle, Edit2 } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { api } from '../api/client';
import { StatusBadge, ImportanceBadge } from './UI';
import TaskModal from './TaskModal';
import { format, isAfter } from 'date-fns';

const TODAY = new Date();

// Faelligkeits-Filter. Die Grenzen werden pro Auswertung frisch berechnet,
// damit ein ueber Mitternacht offenes Board nicht auf gestern haengenbleibt.
const DUE_FILTERS = [
  { value: '', label: 'Alle Fälligkeiten' },
  { value: 'overdue', label: 'Überfällig' },
  { value: 'today', label: 'Heute fällig' },
  { value: 'tomorrow', label: 'Bis morgen' },
  { value: 'week', label: 'Diese Woche (bis So)' },
  { value: 'next7', label: 'Nächste 7 Tage' },
  { value: 'dated', label: 'Mit Datum' },
  { value: 'none', label: 'Ohne Datum' },
];

// Datums-Spalte kommt als '2026-08-31T00:00:00.000Z' — nur den Datumsteil
// verwenden, sonst verschiebt die Zeitzone das Ergebnis um einen Tag.
function dueDateOf(task) {
  const raw = task.next_step_due_date?.split('T')[0];
  if (!raw) return null;
  const [y, m, d] = raw.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function matchesDue(task, filter, isDone) {
  if (!filter) return true;
  const due = dueDateOf(task);
  if (filter === 'none') return !due;
  if (!due) return false;
  if (filter === 'dated') return true;

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dayAfter = (n) => { const d = new Date(today); d.setDate(d.getDate() + n); return d; };

  switch (filter) {
    case 'overdue': return due < today && !isDone(task);
    case 'today': return due.getTime() === today.getTime();
    case 'tomorrow': return due <= dayAfter(1);
    case 'week': {
      // bis zum Sonntag der laufenden ISO-Woche (Mo=1 … So=7)
      const isoDay = today.getDay() === 0 ? 7 : today.getDay();
      return due <= dayAfter(7 - isoDay);
    }
    case 'next7': return due <= dayAfter(7);
    default: return true;
  }
}

export default function KanbanBoard() {
  const { tasks, statusValues, categories, importanceValues, urgencyValues, refreshTasks, isStale, isDone } = useApp();
  const [filters, setFilters] = useState({ importance: '', urgency: '', category: '', due: '' });
  const [editTask, setEditTask] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newTaskStatus, setNewTaskStatus] = useState(null);

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const filtered = useMemo(() => tasks.filter(t => {
    if (filters.importance && t.importance !== filters.importance) return false;
    if (filters.urgency && t.urgency !== filters.urgency) return false;
    if (filters.category && !t.categories?.includes(filters.category)) return false;
    if (!matchesDue(t, filters.due, isDone)) return false;
    return true;
  }), [tasks, filters, isDone]);

  const byStatus = useMemo(() => {
    const map = {};
    statusValues.forEach(s => { map[s] = []; });
    filtered.forEach(t => { if (map[t.status]) map[t.status].push(t); });
    return map;
  }, [filtered, statusValues]);

  const statusColors = {
    'Offen': 'var(--blue)',
    'In Arbeit': 'var(--accent)',
    'Warten auf X': 'var(--purple)',
    'Review': 'var(--orange)',
    'Done': 'var(--green)',
    'Abgebrochen': 'var(--red)',
  };

  const handleDragStart = (e, taskId) => { e.dataTransfer.setData('taskId', taskId); };
  const handleDrop = async (e, status) => {
    const taskId = e.dataTransfer.getData('taskId');
    const task = tasks.find(t => t.id === taskId);
    if (!task || task.status === status) return;
    await api.updateTask(taskId, { ...task, status });
    refreshTasks();
  };

  return (
    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h1 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: 28, flex: 1 }}>Kanban</h1>
        <button className="btn btn-primary" onClick={() => { setNewTaskStatus(statusValues[0]); setShowModal(true); setEditTask(null); }}>
          <Plus size={14} /> Neuer Task
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <select value={filters.importance} onChange={e => setFilter('importance', e.target.value)} style={{ width: 'auto' }}>
          <option value="">Alle Wichtigkeit</option>
          {importanceValues.map(v => <option key={v}>{v}</option>)}
        </select>
        <select value={filters.urgency} onChange={e => setFilter('urgency', e.target.value)} style={{ width: 'auto' }}>
          <option value="">Alle Dringlichkeit</option>
          {urgencyValues.map(v => <option key={v}>{v}</option>)}
        </select>
        {categories.length > 0 && (
          <select value={filters.category} onChange={e => setFilter('category', e.target.value)} style={{ width: 'auto' }}>
            <option value="">Alle Kategorien</option>
            {categories.map(c => <option key={c}>{c}</option>)}
          </select>
        )}
        <select value={filters.due} onChange={e => setFilter('due', e.target.value)} style={{ width: 'auto' }}>
          {DUE_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        <button className="btn btn-ghost btn-sm" onClick={() => setFilters({ importance: '', urgency: '', category: '', due: '' })}>Reset</button>
      </div>

      {/* Columns */}
      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', flex: 1, paddingBottom: 8 }}>
        {statusValues.map(status => (
          <div key={status}
            onDragOver={e => e.preventDefault()}
            onDrop={e => handleDrop(e, status)}
            style={{ minWidth: 260, width: 260, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Column Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: statusColors[status] || 'var(--text-3)', flexShrink: 0 }} />
              <span style={{ fontWeight: 500, fontSize: 13 }}>{status}</span>
              <span style={{ color: 'var(--text-3)', fontSize: 12, marginLeft: 'auto' }}>{byStatus[status]?.length || 0}</span>
              <button className="btn btn-icon btn-ghost btn-sm" title="Task hinzufügen"
                onClick={() => { setNewTaskStatus(status); setEditTask(null); setShowModal(true); }}>
                <Plus size={12} />
              </button>
            </div>

            {/* Cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
              {(byStatus[status] || []).map(task => {
                const overdue = task.next_step_due_date && isAfter(TODAY, new Date(task.next_step_due_date)) && !isDone(task);
                const stale = isStale(task);
                return (
                  <div key={task.id}
                    draggable
                    onDragStart={e => handleDragStart(e, task.id)}
                    style={{
                      background: 'var(--bg-1)', border: `1px solid ${overdue ? 'var(--red)' : 'var(--border)'}`,
                      borderRadius: 'var(--radius)', padding: '10px 12px',
                      cursor: 'grab', opacity: stale ? 0.6 : 1,
                    }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
                      <div style={{ fontWeight: 500, fontSize: 13, flex: 1, lineHeight: 1.4 }}>{task.title}</div>
                      <button className="btn btn-icon btn-ghost btn-sm" onClick={() => { setEditTask(task); setShowModal(true); }}>
                        <Edit2 size={11} />
                      </button>
                    </div>

                    {task.owner && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>👤 {task.owner}</div>}

                    {task.next_step && (
                      <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 6, borderLeft: '2px solid var(--border-2)', paddingLeft: 6 }}>
                        {task.next_step}
                      </div>
                    )}

                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                      <ImportanceBadge value={task.importance} />
                      {task.next_step_due_date && (
                        <span style={{ fontSize: 11, color: overdue ? 'var(--red)' : 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 3 }}>
                          {overdue && <AlertCircle size={10} />}
                          {format(new Date(task.next_step_due_date), 'dd.MM')}
                        </span>
                      )}
                      {(task.categories || []).map(c => (
                        <span key={c} style={{ fontSize: 10, color: 'var(--text-3)', background: 'var(--bg-3)', padding: '1px 5px', borderRadius: 8 }}>{c}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <TaskModal
          task={editTask}
          onClose={() => setShowModal(false)}
          onSaved={refreshTasks}
        />
      )}
    </div>
  );
}
